import { useRef } from 'react';
import { cn } from '@/lib/utils';
import {
  MESSAGE_TAB_LABEL,
  messagePanelId,
  messageTabId,
  type MessageTab,
} from '@/components/message/tabs';

function TextIcon() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
    </svg>
  );
}

function VoiceIcon() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <rect x='9' y='2' width='6' height='12' rx='3' />
      <path d='M5 10v1a7 7 0 0 0 14 0v-1M12 18v4' />
    </svg>
  );
}

function VideoIcon() {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <path d='m23 7-7 5 7 5V7z' />
      <rect x='1' y='5' width='15' height='14' rx='2' />
    </svg>
  );
}

const TAB_ICON: Record<MessageTab, () => React.JSX.Element> = {
  text: TextIcon,
  voice: VoiceIcon,
  video: VideoIcon,
};

type MessageTabsProps = {
  /** Tabs to render, in display order (AC 1: Text, Voice, Video). */
  tabs: readonly MessageTab[];
  selected: MessageTab;
  /**
   * Called with the tab the user activated. The page may refuse the switch
   * (it opens the replace-message confirmation first), so selection is not
   * changed here.
   */
  onSelect: (tab: MessageTab) => void;
  disabled?: boolean;
};

/**
 * Text / Voice / Video selector (OTC prototype, step 3).
 *
 * Manual-activation tab pattern: the arrow keys move focus only and a click
 * or Enter/Space activates. Automatic activation would fire the replace
 * confirmation on every arrow press.
 */
function MessageTabs({
  tabs,
  selected,
  onSelect,
  disabled = false,
}: MessageTabsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const focusTab = (index: number) => {
    const buttons =
      listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons?.[(index + tabs.length) % tabs.length]?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight') focusTab(index + 1);
    else if (e.key === 'ArrowLeft') focusTab(index - 1);
    else if (e.key === 'Home') focusTab(0);
    else if (e.key === 'End') focusTab(tabs.length - 1);
    else return;
    e.preventDefault();
  };

  return (
    <div
      ref={listRef}
      role='tablist'
      aria-label='Message type'
      className='flex gap-2 lg:gap-[9px]'
    >
      {tabs.map((tab, index) => {
        const Icon = TAB_ICON[tab];
        const isSelected = tab === selected;
        return (
          <button
            key={tab}
            type='button'
            role='tab'
            id={messageTabId(tab)}
            aria-selected={isSelected}
            aria-controls={messagePanelId(tab)}
            tabIndex={isSelected ? 0 : -1}
            disabled={disabled}
            data-testid={`message-tab-${tab}`}
            onClick={() => onSelect(tab)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              'flex h-[78px] min-w-0 flex-1 cursor-pointer flex-col items-center justify-center rounded-[13px] border-[1.5px] transition-colors',
              'focus-visible:ring-[3px] focus-visible:ring-[#66bda4]/50 focus-visible:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-60',
              isSelected
                ? 'border-[#256956] bg-white text-[#256956]'
                : 'border-[#f4f3ef] bg-[#f4f3ef] text-[#5a635e]',
            )}
          >
            <Icon />
            <span className='mt-[7px] text-[13px] font-semibold'>
              {MESSAGE_TAB_LABEL[tab]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default MessageTabs;
